import { AuditInstance, SEOModule } from '../types';

export interface ScoreReport {
  overallScore: number;
  onPageScore: number;
  offPageScore: number;
  sectionScores: Record<string, number>;
  scoreSource: 'explicit' | 'configurable';
  weightingSource: 'Lectures 31–33 Framework (On-Page 66%, Off-Page 34%)';
}

export class ScoringEngine {
  /**
   * Calculate audit scores based on collected raw data and structured checklists
   */
  static calculateScores(audit: AuditInstance, module: SEOModule): ScoreReport {
    const sectionScores: Record<string, number> = {};

    let totalOnPageScore = 0;
    let onPageCount = 0;
    let offPageScore = 80; // Default off-page baseline if Moz data collected

    // Helper to get total crawled pages from step 2 / step-titles-total
    const getCrawledPages = (): number => {
      const ans = audit.answers['step-titles-total'];
      const total = Number(ans?.fieldValues?.totalPages || 0);
      return total > 0 ? total : 100;
    };

    const totalCrawledPages = getCrawledPages();

    for (const section of module.sections) {
      let sectionItemsEvaluated = 0;
      let totalPercentageSum = 0;

      for (const step of section.steps) {
        // Hide step-meta-keywords from calculation
        if (step.id === 'step-meta-keywords') continue;

        const answer = audit.answers[step.id];
        if (!answer || !answer.completed) continue;

        const fv = answer.fieldValues || {};

        // 1. Meta Descriptions (Section 3)
        if (step.id === 'step-meta-missing') {
          const count = Number(fv.missingMeta || 0);
          if (count > 0) {
            const pct = (count / totalCrawledPages) * 100;
            const penalty = Math.max(pct * 3, count * 4); // Min 4% penalty per missing description
            totalPercentageSum += penalty;
            sectionItemsEvaluated++;
          }
        } else if (step.id === 'step-meta-long') {
          const count = Number(fv.longMeta || 0);
          if (count > 0) {
            const pct = (count / totalCrawledPages) * 100;
            const penalty = Math.max(pct * 2, count * 3); // Min 3% penalty per long description
            totalPercentageSum += penalty;
            sectionItemsEvaluated++;
          }
        } else if (step.id === 'step-meta-short') {
          const count = Number(fv.shortMeta || 0);
          if (count > 0) {
            const pct = (count / totalCrawledPages) * 100;
            totalPercentageSum += Math.max(pct * 2, count * 2);
            sectionItemsEvaluated++;
          }
        } else if (step.id === 'step-meta-duplicate') {
          const count = Number(fv.duplicateMeta || 0);
          if (count > 0) {
            const pct = (count / totalCrawledPages) * 100;
            totalPercentageSum += Math.max(pct * 2.5, count * 3);
            sectionItemsEvaluated++;
          }
        }
        // 2. Internal Links & Status Codes (Step 21)
        else if (step.id === 'step-internal-links') {
          const count200 = Number(fv.count200Ok || 0);
          const count3xx = Number(fv.count3xxRedirects || 0);
          const count403 = Number(fv.count403Forbidden || 0);
          const count404 = Number(fv.count404Broken || 0);
          const count5xx = Number(fv.count5xxServer || 0);

          const totalLinks = count200 + count3xx + count403 + count404 + count5xx;
          const brokenLinks = count404 + count403 + count5xx;

          if (totalLinks > 0 || brokenLinks > 0) {
            const effectiveTotal = Math.max(totalLinks, brokenLinks);
            const brokenPct = (brokenLinks / effectiveTotal) * 100;
            const brokenPenalty = brokenLinks > 0 ? Math.max(brokenPct * 3, Math.min(brokenLinks * 6, 60)) : 0;
            const redirectPenalty = count3xx > 0 && effectiveTotal > 0 ? Math.min((count3xx / effectiveTotal) * 20, 15) : 0;
            const totalIssuePct = Math.min(100, brokenPenalty + redirectPenalty);

            totalPercentageSum += totalIssuePct;
            sectionItemsEvaluated++;
          }
        }
        // 3. URL Length & Formatting Rules (Step 20)
        else if (step.id === 'step-url-structure') {
          const totalUrls = Number(fv.totalUrls || totalCrawledPages);
          const over115 = Number(fv.urlsOver115 || 0);
          const underscores = Number(fv.urlsWithUnderscores || 0);
          const repetitive = Number(fv.urlsRepetitive || 0);

          if (totalUrls > 0) {
            const over115Pct = (over115 / totalUrls) * 100;
            const underscoresPct = (underscores / totalUrls) * 100;
            const repetitivePct = (repetitive / totalUrls) * 100;
            const totalUrlPenalty = Math.min(100, over115Pct + underscoresPct * 1.5 + repetitivePct * 2);

            totalPercentageSum += totalUrlPenalty;
            sectionItemsEvaluated++;
          }
        }
        // 4. Image Alt Text & Attributes (Step 19)
        else if (step.id === 'step-images-alt') {
          const totalImg = Number(fv.totalImages || 100);
          const missingAlt = Number(fv.missingAltText || 0);
          const missingAttr = Number(fv.missingAltAttribute || 0);
          const missingSize = Number(fv.missingSizeAttributes || 0);

          if (totalImg > 0 || missingAlt > 0) {
            const effectiveTotal = Math.max(totalImg, missingAlt);
            const maxAltMissing = Math.max(missingAlt, missingAttr);
            const altPct = (maxAltMissing / effectiveTotal) * 100;
            const sizePct = (missingSize / effectiveTotal) * 40;
            const imgPenalty = Math.min(100, altPct + sizePct);

            totalPercentageSum += imgPenalty;
            sectionItemsEvaluated++;
          }
        }
        // 5. Image Sizes (Step 18)
        else if (step.id === 'step-images-size') {
          const totalImg = Number(fv.totalImages || 100);
          const over100 = Number(fv.imagesOver100kb || 0);

          if (totalImg > 0 || over100 > 0) {
            const effectiveTotal = Math.max(totalImg, over100);
            const pct = (over100 / effectiveTotal) * 100;
            totalPercentageSum += pct;
            sectionItemsEvaluated++;
          }
        }
        // 6. Keyword Focus (Step 15)
        else if (step.id === 'step-keyword-focus') {
          const pattern = fv.observedKeywordPattern;
          let patternScore = 100;
          if (pattern === 'partial_target') patternScore = 70;
          else if (pattern === 'unclear_target') patternScore = 30;

          totalPercentageSum += (100 - patternScore);
          sectionItemsEvaluated++;
        }
        // 7. Content Growth (Step 17)
        else if (step.id === 'step-static-vs-dynamic') {
          const growth = fv.contentGrowthClassification;
          let growthScore = 100;
          if (growth === 'static_stalled') growthScore = 50;
          else if (growth === 'static_fixed_only') growthScore = 30;

          totalPercentageSum += (100 - growthScore);
          sectionItemsEvaluated++;
        }
        // 8. Google Indexation (Step 25)
        else if (step.id === 'step-indexation-google') {
          const match = fv.googleIndexMatch;
          if (match === 'under_indexed') totalPercentageSum += 40;
          else if (match === 'over_indexed_params') totalPercentageSum += 50;
          sectionItemsEvaluated++;
        }
        // 9. Social Media (Step 32)
        else if (step.id === 'step-social-media') {
          const status = fv.socialPresenceStatus;
          if (status === 'unlinked_broken') totalPercentageSum += 40;
          else if (status === 'no_social_presence') totalPercentageSum += 60;
          sectionItemsEvaluated++;
        }
        // 10. SSL & Security (Step 23–24)
        else if (step.id === 'step-ssl-security') {
          if (fv.sslInstalled === false || fv.sslInstalled === 'no') totalPercentageSum += 100;
          else if (fv.httpsRedirectActive === false || fv.httpsRedirectActive === 'no') totalPercentageSum += 40;
          sectionItemsEvaluated++;
        }
        else if (step.id === 'step-mixed-content') {
          const mixedCount = Number(fv.mixedContentCount || 0);
          if (mixedCount > 0) totalPercentageSum += Math.min(100, mixedCount * 20);
          sectionItemsEvaluated++;
        }
        // 11. Pingdom Speed (Step 30)
        else if (step.id === 'step-pingdom-speed') {
          const loadTime = parseFloat(String(fv.pingdomLoadTime || '0'));
          if (loadTime > 2.0) {
            totalPercentageSum += Math.min(100, (loadTime - 2.0) * 25);
          }
          sectionItemsEvaluated++;
        }
        // 12. Siteliner Duplicate Content (Step 28)
        else if (step.id === 'step-siteliner') {
          if (fv.sitelinerAccessStatus === 'accessible') {
            const dupPct = parseFloat(String(fv.sitelinerDuplicatePct || '0'));
            if (dupPct > 15) {
              totalPercentageSum += Math.min(100, (dupPct - 15) * 3);
            }
          }
          sectionItemsEvaluated++;
        }
        // 13. W3C Validation (Step 29)
        else if (step.id === 'step-w3c-validation') {
          const errors = Number(fv.w3cErrorCount || 0);
          if (errors > 0) {
            totalPercentageSum += Math.min(100, errors * 5);
          }
          sectionItemsEvaluated++;
        }
        // 14. H1 Headings Audit (Step 13)
        else if (step.id === 'step-h1-audit') {
          const missingH1 = Number(fv.missingH1 || 0);
          const duplicateH1 = Number(fv.duplicateH1 || 0);
          const multipleH1 = Number(fv.multipleH1 || 0);

          if (totalCrawledPages > 0) {
            const h1IssuePct = Math.min(100, ((missingH1 * 2 + duplicateH1 + multipleH1) / totalCrawledPages) * 100);
            totalPercentageSum += h1IssuePct;
            sectionItemsEvaluated++;
          }
        }
        // 15. Moz Domain Authority (Step 31)
        else if (step.id === 'step-moz-authority') {
          const da = Number(fv.domainAuthority);
          if (!isNaN(da)) {
            offPageScore = Math.min(100, Math.max(0, da));
          }
        }
        // 16. Generic check across step.calculations
        else if (step.calculations && step.calculations.length > 0) {
          for (const calc of step.calculations) {
            const pctVal = answer.calculatedValues[calc.id];
            if (typeof pctVal === 'number' && !isNaN(pctVal)) {
              totalPercentageSum += pctVal;
              sectionItemsEvaluated++;
            }
          }
        }
      }

      let sectionScore = 100;
      if (sectionItemsEvaluated > 0) {
        const avgIssuePct = totalPercentageSum / sectionItemsEvaluated;
        sectionScore = Math.max(0, Math.round(100 - avgIssuePct));
      }

      // Special override for Off-Page SEO & Social Media section
      if (section.id === 'sec-offpage-social') {
        const mozAns = audit.answers['step-moz-authority'];
        const daVal = Number(mozAns?.fieldValues?.domainAuthority || offPageScore);
        const socialAns = audit.answers['step-social-media'];
        const socialStatus = socialAns?.fieldValues?.socialPresenceStatus;

        let socialScore = 100;
        if (socialStatus === 'unlinked_broken') socialScore = 60;
        else if (socialStatus === 'no_social_presence') socialScore = 40;

        // Off-page section score combines DA (80% weight) and social presence (20% weight)
        sectionScore = Math.round(daVal * 0.8 + socialScore * 0.2);
        offPageScore = sectionScore;
      }

      sectionScores[section.id] = sectionScore;

      if (section.id !== 'sec-offpage-social') {
        totalOnPageScore += sectionScore;
        onPageCount++;
      }
    }

    const avgOnPageScore = onPageCount > 0 ? Math.round(totalOnPageScore / onPageCount) : 100;

    const onPageWeight = module.weightingRules?.onPageWeight || 0.66;
    const offPageWeight = module.weightingRules?.offPageWeight || 0.34;

    const overallScore = Math.round(avgOnPageScore * onPageWeight + offPageScore * offPageWeight);

    return {
      overallScore,
      onPageScore: avgOnPageScore,
      offPageScore: Math.round(offPageScore),
      sectionScores,
      scoreSource: 'configurable',
      weightingSource: 'Lectures 31–33 Framework (On-Page 66%, Off-Page 34%)',
    };
  }
}
